import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';

const VideoCard = ({ video, variant = 'default' }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const getYouTubeId = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtube\.com\/embed\/)([^?\s]+)/,
      /(?:youtu\.be\/)([^?\s]+)/,
      /(?:youtube\.com\/v\/)([^?\s]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const videoId = getYouTubeId(video.youtube_url);
  const thumbnailUrl = videoId 
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : video.image_url;

  if (variant === 'featured') {
    return (
      <div className="relative rounded-xl overflow-hidden bg-slate-900" data-testid={`featured-video-${video.id}`}>
        <div className="aspect-video relative">
          {isPlaying && videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="absolute inset-0 cursor-pointer group"
              onClick={() => setIsPlaying(true)}
            >
              <img
                src={thumbnailUrl}
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = video.image_url; }}
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                <div className="bg-red-600 rounded-full p-5 transition-transform group-hover:scale-110 shadow-2xl">
                  <Play className="w-12 h-12 text-white fill-white" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-6">
          <Link to={`/haber/${video.id}`}>
            <h3 className="text-xl md:text-2xl font-bold text-white leading-tight hover:text-[#38BDF8] transition-colors">
              {video.title}
            </h3>
          </Link>
          <p className="text-gray-400 text-sm mt-3 line-clamp-2">
            {video.body ? video.body.substring(0, 150) + '...' : ''}
          </p>
        </div>
      </div>
    );
  }

  // Default variant — thumbnail + title + short body preview below
  return (
    <Link
      to={`/haber/${video.id}`}
      className="group block rounded-lg overflow-hidden bg-white shadow-sm ring-1 ring-gray-100 hover:shadow-lg hover:ring-gray-200 transition-all"
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video bg-slate-800">
        <img
          src={thumbnailUrl}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { e.target.src = video.image_url; }}
        />
        <div className="absolute inset-0 bg-black/25 flex items-center justify-center opacity-100 group-hover:bg-black/10 transition-colors">
          <div className="bg-red-600 rounded-full p-3 transition-transform group-hover:scale-110 shadow-xl">
            <Play className="w-6 h-6 text-white fill-white" />
          </div>
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-bold text-base text-[#0F172A] leading-snug line-clamp-2 group-hover:text-red-600 transition-colors">
          {video.title}
        </h4>
        {video.body && (
          <p className="text-gray-500 text-sm mt-2 leading-relaxed line-clamp-3">
            {video.body}
          </p>
        )}
      </div>
    </Link>
  );
};

export default VideoCard;
